import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  IconButton,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  Menu,
  MenuItem
} from '@mui/material';
import {
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  CheckCircle as CheckCircleIcon,
  Event as EventIcon,
  Chat as ChatIcon,
  PersonAdd as PersonAddIcon,
  PersonRemove as PersonRemoveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { useNotifications } from '../contexts/NotificationContext';
import { Notification, NotificationType } from '../utils/api';
import { useNavigate } from 'react-router-dom';

/** Display item: either a single notification or a grouped new_message row (same thread) */
type DisplayItem =
  | { kind: 'single'; notification: Notification }
  | {
      kind: 'grouped';
      thread_id: string;
      event_id?: string | null;
      notification_ids: string[];
      count: number;
      title: string;
      body: string;
      created_at: string;
      is_read: boolean;
    };
// Simple date formatter
const formatTimeAgo = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  
  if (diffInSeconds < 60) return 'just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
  return date.toLocaleDateString();
};

interface NotificationPanelProps {
  onClose?: () => void;
}

const NotificationPanel: React.FC<NotificationPanelProps> = ({ onClose }) => {
  const {
    notifications,
    unreadCount,
    isLoading,
    error,
    refreshNotifications,
    markAsRead,
    markSingleAsRead,
    deleteNotification,
    deleteAllNotifications
  } = useNotifications();

  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedNotification, setSelectedNotification] = useState<string | null>(null);

  // Part B: Group new_message notifications by thread_id for display
  const displayList = useMemo((): DisplayItem[] => {
    const newMessageByThread = new Map<string, Notification[]>();
    const others: Notification[] = [];
    for (const n of notifications) {
      if (n.type === 'new_message' && n.thread_id?.trim()) {
        const tid = n.thread_id.trim();
        if (!newMessageByThread.has(tid)) newMessageByThread.set(tid, []);
        newMessageByThread.get(tid)!.push(n);
      } else {
        others.push(n);
      }
    }
    const result: DisplayItem[] = [];
    newMessageByThread.forEach((group, thread_id) => {
      const sorted = [...group].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      const latest = sorted[0];
      const eventMatch = latest.title.match(/in '([^']*)'/);
      const eventName = eventMatch ? eventMatch[1] : 'Event';
      const count = group.length;
      const title =
        count > 1 ? `${count} new messages in '${eventName}'` : latest.title;
      result.push({
        kind: 'grouped',
        thread_id,
        event_id: latest.event_id,
        notification_ids: group.map((n) => n.id),
        count,
        title,
        body: latest.body,
        created_at: latest.created_at,
        is_read: group.every((n) => n.is_read),
      });
    });
    const combined: DisplayItem[] = [...result];
    others.forEach((n) => combined.push({ kind: 'single', notification: n }));
    combined.sort((a, b) => {
      const tA = a.kind === 'grouped' ? a.created_at : a.notification.created_at;
      const tB = b.kind === 'grouped' ? b.created_at : b.notification.created_at;
      return new Date(tB).getTime() - new Date(tA).getTime();
    });
    return combined;
  }, [notifications]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, notificationId: string) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedNotification(notificationId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedNotification(null);
  };

  const handleMarkAsRead = async (notificationId: string) => {
    try {
      await markSingleAsRead(notificationId);
      handleMenuClose();
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const handleDelete = async (notificationId: string) => {
    try {
      await deleteNotification(notificationId);
      handleMenuClose();
    } catch (err) {
      console.error('Error deleting notification:', err);
    }
  };

  const handleDeleteAll = async () => {
    try {
      await deleteAllNotifications(true); // Delete only read notifications
      handleMenuClose();
    } catch (err) {
      console.error('Error deleting notifications:', err);
    }
  };

  const getTarget = (item: DisplayItem): { threadId?: string; eventId?: string; requestId?: string } => {
    if (item.kind === 'grouped') {
      return { threadId: item.thread_id?.trim(), eventId: item.event_id?.trim() };
    }
    const n = item.notification;
    return {
      threadId: n.thread_id?.trim(),
      eventId: n.event_id?.trim(),
      requestId: n.request_id?.trim(),
    };
  };

  const hasTarget = (item: DisplayItem): boolean => {
    const t = getTarget(item);
    return !!(t.threadId || t.eventId || t.requestId);
  };

  const handleItemClick = async (item: DisplayItem) => {
    if (item.kind === 'grouped') {
      if (!item.is_read && item.notification_ids.length > 0) {
        await markAsRead(item.notification_ids);
      }
    } else {
      const n = item.notification;
      if (!n.is_read) {
        await markSingleAsRead(n.id);
      }
    }

    const { threadId, eventId, requestId } = getTarget(item);
    if (threadId) {
      navigate(`/chat?thread=${threadId}`);
    } else if (eventId) {
      navigate(`/event/${eventId}`);
    } else if (requestId) {
      navigate('/inbox');
    } else {
      navigate('/');
    }
    onClose?.();
  };

  const getNotificationIcon = (type: NotificationType) => {
    switch (type) {
      case 'event_join_request':
      case 'event_join_accepted':
      case 'event_joined':
        return <PersonAddIcon fontSize="small" />;
      case 'event_left':
        return <PersonRemoveIcon fontSize="small" />;
      case 'event_join_declined':
      case 'event_canceled':
        return <CancelIcon fontSize="small" />;
      case 'new_message':
        return <ChatIcon fontSize="small" />;
      default:
        return <EventIcon fontSize="small" />;
    }
  };

  const getNotificationColor = (type: NotificationType) => {
    switch (type) {
      case 'event_join_request':
        return 'info';
      case 'event_join_accepted':
      case 'event_joined':
        return 'success';
      case 'event_join_declined':
      case 'event_canceled':
        return 'error';
      case 'event_left':
        return 'warning';
      case 'new_message':
        return 'primary';
      default:
        return 'default';
    }
  };

  if (isLoading && notifications.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Notifications</Typography>
        <Box>
          {unreadCount > 0 && (
            <Chip label={`${unreadCount} unread`} size="small" color="error" sx={{ mr: 1 }} />
          )}
          <IconButton
            size="small"
            onClick={(e) => handleMenuOpen(e, '')}
            aria-label="more options"
          >
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl) && selectedNotification === ''}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={handleDeleteAll}>Clear Read</MenuItem>
            <MenuItem onClick={refreshNotifications}>Refresh</MenuItem>
          </Menu>
        </Box>
      </Box>
      <Divider />
      {displayList.length === 0 ? (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No notifications
          </Typography>
        </Box>
      ) : (
        <List sx={{ maxHeight: '60vh', overflow: 'auto' }}>
          {displayList.map((item, index) => {
            const isGrouped = item.kind === 'grouped';
            const isRead = isGrouped ? item.is_read : item.notification.is_read;
            const type: NotificationType = isGrouped ? 'new_message' : item.notification.type;
            const title = isGrouped ? item.title : item.notification.title;
            const body = isGrouped ? item.body : item.notification.body;
            const created_at = isGrouped ? item.created_at : item.notification.created_at;
            const rowKey = isGrouped ? `grouped-${item.thread_id}` : item.notification.id;
            const menuKey = isGrouped ? `grouped-${item.thread_id}` : item.notification.id;
            const clickable = hasTarget(item);

            return (
              <React.Fragment key={rowKey}>
                <ListItem
                  disablePadding
                  sx={{
                    bgcolor: isRead ? 'transparent' : 'action.hover',
                    '&:hover': {
                      bgcolor: clickable ? 'action.selected' : 'action.hover'
                    }
                  }}
                >
                  <ListItemButton
                    onClick={() => handleItemClick(item)}
                    sx={{
                      py: 1.5,
                      cursor: clickable ? 'pointer' : 'default',
                      '&:hover': {
                        bgcolor: clickable ? 'action.selected' : 'transparent'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
                      <Box sx={{ mr: 2, mt: 0.5 }}>
                        {getNotificationIcon(type)}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: isRead ? 400 : 600,
                              flex: 1
                            }}
                          >
                            {title}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, menuKey)}
                            sx={{ ml: 1 }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mb: 0.5,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical'
                          }}
                        >
                          {body}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Chip
                            label={type.replace(/_/g, ' ')}
                            size="small"
                            color={getNotificationColor(type) as any}
                            sx={{ fontSize: '0.7rem', height: 20 }}
                          />
                          {isGrouped && item.count > 1 && (
                            <Chip label={`${item.count}`} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />
                          )}
                          <Typography variant="caption" color="text.secondary">
                            {formatTimeAgo(created_at)}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </ListItemButton>
                </ListItem>
                {index < displayList.length - 1 && <Divider />}
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl) && selectedNotification === menuKey}
                  onClose={handleMenuClose}
                >
                  {!isRead && (
                    <MenuItem
                      onClick={async () => {
                        if (isGrouped) {
                          await markAsRead(item.notification_ids);
                        } else {
                          await handleMarkAsRead(item.notification.id);
                        }
                        handleMenuClose();
                      }}
                    >
                      <CheckCircleIcon sx={{ mr: 1, fontSize: 18 }} />
                      Mark as read
                    </MenuItem>
                  )}
                  <MenuItem
                    onClick={async () => {
                      if (isGrouped) {
                        for (const id of item.notification_ids) {
                          await deleteNotification(id);
                        }
                      } else {
                        await handleDelete(item.notification.id);
                      }
                      handleMenuClose();
                    }}
                  >
                    <DeleteIcon sx={{ mr: 1, fontSize: 18 }} />
                    Delete
                  </MenuItem>
                </Menu>
              </React.Fragment>
            );
          })}
        </List>
      )}
    </Box>
  );
};

export default NotificationPanel;
